import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Cancel,
  Delete,
  ZoomIn,
  Edit,
  Warning,
} from '@mui/icons-material';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ConsolidatedSkill } from '@/services/workersV2API';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import UploadCertificateModal from './UploadCertificateModal';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusBadge = (
  status: string,
): 'warning' | 'theme' | 'danger' | 'neutral' => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'verified':
      return 'theme';
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
};

const isExpired = (expiresAt: string | null): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

const isExpiringSoon = (expiresAt: string | null): boolean => {
  if (!expiresAt) return false;
  const expDate = new Date(expiresAt);
  return (
    expDate >= new Date() &&
    expDate < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  );
};

const CertificatesTab = ({ skill, workerId, onUpdate }: Props) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [expiryDialog, setExpiryDialog] = useState<{
    open: boolean;
    certId: number | null;
    value: string;
  }>({ open: false, certId: null, value: '' });

  const certificates = skill.skill_request?.certificates || [];

  const handleVerify = async (id: number) => {
    setProcessing(true);
    try {
      await axiosRubyClient.post(`/api/admin/certificate_photos/${id}/verify`);
      onUpdate();
    } catch (error) {
      console.error('Error verifying certificate:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (id: number) => {
    setProcessing(true);
    try {
      await axiosRubyClient.post(`/api/admin/certificate_photos/${id}/reject`);
      onUpdate();
    } catch (error) {
      console.error('Error rejecting certificate:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setProcessing(true);
    try {
      await axiosRubyClient.delete(`/api/admin/certificate_photos/${deleteId}`);
      setDeleteId(null);
      onUpdate();
    } catch (error) {
      console.error('Error deleting certificate:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveExpiry = async () => {
    if (!expiryDialog.certId) return;
    setProcessing(true);
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/certificate_photos/${expiryDialog.certId}`,
        { expires_at: expiryDialog.value || null },
      );
      if (response.data.success) {
        setExpiryDialog({ open: false, certId: null, value: '' });
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating expiry:', error);
    } finally {
      setProcessing(false);
    }
  };

  const openExpiryDialog = (certId: number, currentExpiry: string | null) => {
    setExpiryDialog({
      open: true,
      certId,
      value: currentExpiry
        ? new Date(currentExpiry).toISOString().split('T')[0]
        : '',
    });
  };

  return (
    <>
      <Card>
        <Card.Section>
          <Stack
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Body weight="semibold">Certificates ({certificates.length})</Body>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setUploadOpen(true)}
            >
              Upload
            </Button>
          </Stack>
        </Card.Section>

        {certificates.length === 0 ? (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">No certificates uploaded yet</Small>
            </Stack>
          </Card.Section>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expires At</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificates.map((cert) => {
                  const expired = isExpired(cert.expires_at);
                  const expiringSoon = isExpiringSoon(cert.expires_at);

                  return (
                    <TableRow key={cert.id}>
                      <TableCell>{cert.id}</TableCell>
                      <TableCell>
                        {cert.type
                          ? cert.type
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (c) => c.toUpperCase())
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          label={
                            cert.status.charAt(0).toUpperCase() +
                            cert.status.slice(1)
                          }
                          status={getStatusBadge(cert.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {cert.expires_at ? (
                          <Stack gap={4} style={{ alignItems: 'center' }}>
                            <span
                              style={{
                                color: expired
                                  ? '#EF4444'
                                  : expiringSoon
                                    ? '#F59E0B'
                                    : undefined,
                                fontWeight:
                                  expired || expiringSoon ? 600 : undefined,
                              }}
                            >
                              {formatDate(cert.expires_at)}
                              {expired && ' (Expired)'}
                              {expiringSoon && ' (Soon)'}
                            </span>
                            {(expired || expiringSoon) && (
                              <Warning
                                sx={{
                                  fontSize: 16,
                                  color: expired ? '#EF4444' : '#F59E0B',
                                }}
                              />
                            )}
                          </Stack>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {cert.image_url ? (
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              backgroundColor: '#F3F4F6',
                              position: 'relative',
                            }}
                            onClick={() => setPreviewImage(cert.image_url)}
                          >
                            <img
                              src={cert.image_url}
                              alt="Certificate"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.opacity = '0';
                              }}
                            >
                              <ZoomIn
                                style={{ color: '#fff', fontSize: '18px' }}
                              />
                            </div>
                          </div>
                        ) : (
                          <Small color="lighter">-</Small>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(cert.created_at)}</TableCell>
                      <TableCell>
                        <Stack gap={4}>
                          <IconButton
                            size="small"
                            color="success"
                            title="Verify"
                            onClick={() => handleVerify(cert.id)}
                            disabled={processing}
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title="Reject"
                            onClick={() => handleReject(cert.id)}
                            disabled={processing}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Edit Expiry"
                            onClick={() =>
                              openExpiryDialog(cert.id, cert.expires_at)
                            }
                            disabled={processing}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title="Delete"
                            onClick={() => setDeleteId(cert.id)}
                            disabled={processing}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent style={{ padding: 0 }}>
          <img
            src={previewImage || ''}
            alt="Certificate"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewImage(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Certificate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this certificate? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={processing}
          >
            {processing ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Expiry Dialog */}
      <Dialog
        open={expiryDialog.open}
        onClose={() =>
          setExpiryDialog({ open: false, certId: null, value: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Expiry Date</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Set the expiration date for this certificate.
          </DialogContentText>
          <TextField
            type="date"
            fullWidth
            value={expiryDialog.value}
            onChange={(e) =>
              setExpiryDialog({ ...expiryDialog, value: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            label="Expiry Date"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setExpiryDialog({ open: false, certId: null, value: '' })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveExpiry}
            variant="contained"
            color="primary"
            disabled={processing}
          >
            {processing ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Certificate Modal */}
      <UploadCertificateModal
        open={uploadOpen}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        availableCertificateTypes={skill.required_certificate_types}
        onClose={() => setUploadOpen(false)}
        onSuccess={onUpdate}
      />
    </>
  );
};

export default CertificatesTab;
